import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Activity,
  AlertTriangle,
  Banknote,
  BarChart3,
  Building2,
  FileText,
  LayoutDashboard,
  LayoutTemplate,
  ListChecks,
  LogOut,
  Megaphone,
  Package,
  ScrollText,
  UserCog,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useUserContext } from '@/lib/auth/useUserContext';
import { usePinSession } from '@/lib/auth/pin/usePinSession';
import { canManageBaristaUsers } from '@/lib/auth/rbac';
import { ScopePicker, ViewScopeProvider } from '@/lib/scope/ViewScope';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** When set, the link only renders if the predicate passes for the user's groups. */
  gate?: (groups: readonly string[]) => boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/cash-requests', label: 'Cash', icon: Banknote },
  { to: '/inventory-requests', label: 'Inventory', icon: Package },
  { to: '/tasks', label: 'Tasks', icon: ListChecks },
  { to: '/incidents', label: 'Incidents', icon: AlertTriangle },
  { to: '/reports', label: 'Reports', icon: BarChart3 },
  { to: '/documents', label: 'Documents', icon: FileText },
  { to: '/announcements', label: 'Announcements', icon: Megaphone },
  { to: '/admin/audit', label: 'Audit', icon: ScrollText, gate: canManageBaristaUsers },
  { to: '/admin/users', label: 'Users', icon: Users, gate: canManageBaristaUsers },
  { to: '/admin/hr', label: 'HR', icon: UserCog, gate: canManageBaristaUsers },
  { to: '/admin/locations', label: 'Locations', icon: Building2, gate: canManageBaristaUsers },
  {
    to: '/admin/dashboard-layouts',
    label: 'Layouts',
    icon: LayoutTemplate,
    gate: canManageBaristaUsers,
  },
  { to: '/admin/health', label: 'Health', icon: Activity, gate: canManageBaristaUsers },
];

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors',
    isActive
      ? 'bg-primary text-primary-foreground shadow-sm'
      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
  );
}

/**
 * Shared chrome for the authenticated Tier-1 (manager/Entra) pages: a persistent
 * role-aware navigation bar plus the "Signed in as …" status line and sign-out
 * action. Wraps the Tier-1 routes via <Outlet />. Must be nested inside
 * <RequireAuth>. Tier-2 kiosk screens deliberately do NOT use this layout.
 */
export function Tier1Layout() {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const { logout: pinLogout } = usePinSession();
  const { user, tier, primaryRole, roles, scopedStores, groups } = useUserContext();

  const visibleItems = NAV_ITEMS.filter((item) => !item.gate || item.gate(groups));
  const who = user?.username ?? primaryRole ?? '—';
  const initial = who.charAt(0).toUpperCase();

  function signOut() {
    if (tier === 'store_ops') {
      pinLogout();
      navigate('/lock');
      return;
    }
    void instance.logoutRedirect({ postLogoutRedirectUri: '/lock' });
  }

  return (
    <ViewScopeProvider>
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 sm:px-6">
            <div className="flex items-center gap-2">
              <img src="/favicon.svg" alt="Qargo Coffee" className="size-8" />
              <div className="leading-tight">
                <p className="text-sm font-semibold tracking-tight">Qargo IMS</p>
                <p className="text-[11px] text-muted-foreground">TMKN Operations</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <ScopePicker />
              <div className="hidden text-right sm:block">
                <p className="text-xs font-medium">{who}</p>
                <p className="text-[11px] text-muted-foreground">
                  {primaryRole ?? 'no role'}
                  {roles.length > 1 ? ` +${roles.length - 1}` : ''} · {scopedStores.length} store
                  {scopedStores.length === 1 ? '' : 's'}
                </p>
              </div>
              <span className="grid size-8 place-items-center rounded-full bg-accent text-sm font-semibold text-accent-foreground">
                {initial}
              </span>
              <button
                type="button"
                onClick={signOut}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <LogOut className="size-3.5" aria-hidden />
                <span className="hidden sm:inline">
                  {tier === 'store_ops' ? 'End shift' : 'Sign out'}
                </span>
              </button>
            </div>
          </div>
          <nav
            aria-label="Primary"
            className="flex items-center gap-1 overflow-x-auto px-2 pb-2 sm:px-4"
          >
            {visibleItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink key={item.to} to={item.to} className={navLinkClass} end>
                  <Icon className="size-4" aria-hidden />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </header>
        <main className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
          <Outlet />
        </main>
      </div>
    </ViewScopeProvider>
  );
}
